import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import CheckListItem from './ChecklistItem';
import LoadingSpinner from '../../components/LoadingSpinner';
import FormCheckBox from '../../components/FormCheckBox';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';

// TYPES
import { ChecklistTemplateEditCommand } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const ContentChecklistTemplatesDetail: React.FC = () => {
	const history = useHistory();

	// Get all Payment Plans from Redux store
	const paymentPlans = useAppSelector((state) => state.lists.paymentPlans);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get Id from params
	const params = useParams<RouteParams>();

	// Set up data state and initial data
	const initialItemsState = {
		id: undefined,
		title: undefined,
		usage: undefined,
		checklistItems: [],
		selectedPlans: []
	};

	const initialRenderedItemsState = [''];

	const [safetyChecklistsTemplateItemsDetailData, setSafetyChecklistsTemplateItemsDetailData] =
		React.useState<ChecklistTemplateEditCommand>(initialItemsState);

	const [renderedChecklistItems, setRenderedChecklistItems] = React.useState(initialRenderedItemsState);

	const getSafetyChecklistsTemplateItemsData = async () => {
		// Detect if creating a checklist from a template ( with route id)
		if (params.id) {
			setIsLoading(true);

			const response = await apibridge.getChecklistTemplateEdit(params.id);

			response.result = response.result || initialItemsState;

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				setRenderedChecklistItems(response.result.checklistItems);

				setSafetyChecklistsTemplateItemsDetailData(response.result);
			}
		} else {
			// Detect if creating a checklist from blank ( without route id)
			setIsLoading(false);

			const data = initialItemsState;

			const { checklistItems } = data;

			setRenderedChecklistItems(checklistItems);

			setSafetyChecklistsTemplateItemsDetailData(data);
		}
	};

	React.useEffect(() => {
		getSafetyChecklistsTemplateItemsData();
	}, []);

	// SET UP GENERAL FORM
	const validationSchema = Yup.object({
		titleInput: Yup.string().required('Required'),
		usageInput: Yup.string().required('Required'),
		selectedPlansInput: Yup.array().min(1, 'Required')
	});

	const initialValues = {
		id: safetyChecklistsTemplateItemsDetailData.id || '',
		titleInput: safetyChecklistsTemplateItemsDetailData.title || '',
		usageInput: safetyChecklistsTemplateItemsDetailData.usage || '',
		selectedPlansInput: safetyChecklistsTemplateItemsDetailData.selectedPlans || []
	};

	// Handling when we drag
	const onDragEndHandler = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(renderedChecklistItems);

		const [reorderedItem] = items.splice(result.source.index, 1);

		items.splice(result.destination.index, 0, reorderedItem);

		setRenderedChecklistItems(items);
	};

	// ON SUBMIT
	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const checklistData: ChecklistTemplateEditCommand = {
			id: values.id,
			title: values.titleInput,
			usage: values.usageInput,
			checklistItems: [...renderedChecklistItems],
			selectedPlans: values.selectedPlansInput
		};

		const response = await apibridge.postChecklistTemplateEdit(checklistData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/content-checklist-templates');
		}
	};

	const addChecklistHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		const newArray = [...renderedChecklistItems, ''];

		setRenderedChecklistItems(newArray);
	};

	const removeHandler = (itemIndex: number) => {
		const newArray = renderedChecklistItems.filter((item, index) => index !== itemIndex);

		setRenderedChecklistItems(newArray);
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<SwitchTransition mode="out-in">
								<CSSTransition
									key={`key_${isLoading}`}
									timeout={600}
									classNames="standalone-fade"
									mountOnEnter
									unmountOnExit
								>
									{isLoading ? (
										<LoadingSpinner show={isLoading} />
									) : (
										<>
											<div className="general-form-layout-wrapper">
												<div className="form-main w-100">
													<div className="form-group ">
														<label htmlFor="titleInput" className="label">
															Template Title
														</label>

														<div className="form-field">
															<Field name="titleInput" id="titleInput" type="text" className="form-input" />

															<ErrorMessage name="titleInput" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group ">
														<label htmlFor="usageInput" className="label">
															Intended usage
														</label>

														<div className="form-field">
															<Field name="usageInput" id="usageInput" type="text" className="form-input" />

															<ErrorMessage name="usageInput" component="div" className="error-message" />
														</div>
													</div>

													<div>
														<label className="label d-block">Checklist items</label>

														{renderedChecklistItems && renderedChecklistItems.length > 0 && (
															<DragDropContext onDragEnd={onDragEndHandler}>
																<Droppable droppableId="checklistItem">
																	{(provided: DroppableProvided) => (
																		<ul {...provided.droppableProps} ref={provided.innerRef}>
																			{renderedChecklistItems.map((item, index) => {
																				return (
																					<Draggable key={index} draggableId={index.toString()} index={index}>
																						{(provided) => (
																							<li
																								className="mbtom-16 "
																								ref={provided.innerRef}
																								{...provided.draggableProps}
																								{...provided.dragHandleProps}
																							>
																								<CheckListItem
																									item={item}
																									index={index}
																									renderedChecklistItems={renderedChecklistItems}
																									setRenderedChecklistItems={setRenderedChecklistItems}
																									removeHandler={removeHandler}
																								/>
																							</li>
																						)}
																					</Draggable>
																				);
																			})}
																			{provided.placeholder}
																		</ul>
																	)}
																</Droppable>
															</DragDropContext>
														)}
														<Button
															className="btn-text-icon mbtom-40"
															buttonIcon={<PlusIcon className="link-icon" />}
															onClick={addChecklistHandler}
														>
															Add another item
														</Button>
													</div>

													<div className="form-group">
														<label htmlFor="selectedPlansInput" className="label mbtom-16 mtop-24">
															Organisations with access
														</label>

														<div className="form-field">
															<FormCheckBox
																values={safetyChecklistsTemplateItemsDetailData.selectedPlans || []}
																options={paymentPlans}
																onChange={(vals: string[]) => formik.setFieldValue('selectedPlansInput', vals)}
															/>

															<ErrorMessage name="selectedPlansInput" component="div" className="error-message" />
														</div>
													</div>
												</div>
											</div>

											<div className="general-divider mtop-30"></div>

											<div className="form-button-group">
												<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
													Save
												</Button>
											</div>
										</>
									)}
								</CSSTransition>
							</SwitchTransition>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default ContentChecklistTemplatesDetail;
